import React, { ComponentType, ReactElement } from 'react';
import { AlertBlock, SpacerVertical } from '@klarna/bubble-ui';
import { TermsAndConditions } from '@/bpc/components/TermsAndConditions';
import {
  BpcPayload,
  TermsAndConditionsPayload,
  TermsAndConditionsPerMarket,
} from '@/bpc/types/BpcPayload';
import { ErrorMessage } from '@/components/ErrorMessage';

interface WithTermsAndConditionsState {
  selectedTerms: string[];
  hasTermsToAgree: boolean;
}

export interface WithTermsAndConditionsProps {
  t: (key: string) => string;
  bpcPayloadWithTerms: BpcPayload;
  hasPreviousGroup?: boolean;
  hasNextGroup?: boolean;
  submitTermsAndConditions: (terms: TermsAndConditionsPayload) => Promise<void>;
}

export interface WithTermsAndConditionsChildProps {
  TermsAndConditionsComponent: TermsAndConditionsComponent;
  selectedTerms: string[];
  missingTermsValidation: boolean;
  hasTermsToAgree: boolean;
  termsData: TermsAndConditionsPerMarket[];
  onSubmitTermsAndConditionsFn: () => Promise<void>;
}

export const WithTermsAndConditions = <P,>(
  WrappedComponent: ComponentType<P & WithTermsAndConditionsChildProps>,
): ComponentType<P & WithTermsAndConditionsProps> =>
  class extends React.Component<P & WithTermsAndConditionsProps, WithTermsAndConditionsState> {
    constructor(props: P & WithTermsAndConditionsProps) {
      super(props);

      const { bpcPayloadWithTerms } = props;

      const {
        must_agree_terms_and_conditions_before_underwriting: mustAgreeToTerms,
        terms_and_conditions: termsData = [],
      } = bpcPayloadWithTerms || {};

      this.state = {
        selectedTerms: [],
        hasTermsToAgree: !!mustAgreeToTerms && !!termsData.length,
      };

      this.onCheckTermsAndConditions = this.onCheckTermsAndConditions.bind(this);
      this.shouldShowTermsAndConditions = this.shouldShowTermsAndConditions.bind(this);
      this.missingTermsValidation = this.missingTermsValidation.bind(this);
      this.onSubmitTermsAndConditions = this.onSubmitTermsAndConditions.bind(this);
    }

    private missingTermsValidation(): boolean {
      const { bpcPayloadWithTerms } = this.props;
      const { selectedTerms } = this.state;

      const {
        must_agree_terms_and_conditions_before_underwriting: mustAgreeToTerms,
        terms_and_conditions: termsData = [],
        has_agreed_terms_and_conditions: hasAcceptedTerms,
      } = bpcPayloadWithTerms || {};
      const hasTermsToAgree = !!mustAgreeToTerms && !!termsData.length;

      return (
        this.shouldShowTermsAndConditions() &&
        ((hasTermsToAgree && selectedTerms.length !== termsData.length) || !!hasAcceptedTerms)
      );
    }

    private shouldShowTermsAndConditions(): boolean {
      const { hasPreviousGroup, hasNextGroup, bpcPayloadWithTerms } = this.props;

      const {
        must_agree_terms_and_conditions_before_underwriting: mustAgreeToTerms,
      } = bpcPayloadWithTerms;

      /**
       * Meaning is a single group
       */
      return !!mustAgreeToTerms && !hasPreviousGroup && !hasNextGroup;
    }

    private onCheckTermsAndConditions(termUrl: string): () => void {
      const { state, setState: unboundSetState } = this;

      const setState = unboundSetState.bind(this);

      return (): void => {
        const { selectedTerms } = state;
        let updatedTerms = selectedTerms;
        const alreadySelected = selectedTerms.includes(termUrl);
        if (alreadySelected) {
          updatedTerms = updatedTerms.filter(url => url !== termUrl);
        } else {
          updatedTerms = [...selectedTerms, termUrl];
        }

        setState({
          selectedTerms: updatedTerms,
        });
      };
    }

    private onSubmitTermsAndConditions(): Promise<void> {
      const { bpcPayloadWithTerms, submitTermsAndConditions } = this.props;
      const { terms_and_conditions = [] } = bpcPayloadWithTerms || {};

      if (this.missingTermsValidation()) {
        return Promise.reject(new Error('Terms and conditions missing or already approved'));
      }

      return submitTermsAndConditions({ terms_and_conditions });
    }

    render() {
      const { t, bpcPayloadWithTerms } = this.props;

      const { selectedTerms } = this.state;

      const {
        terms_and_conditions: termsData = [],
        has_agreed_terms_and_conditions: hasAcceptedTerms,
      } = bpcPayloadWithTerms || {};

      const shouldShowTermsAndConditions = this.shouldShowTermsAndConditions();
      const enhancedProps = {
        ...this.props,
        onSubmitTermsAndConditionsFn: this.onSubmitTermsAndConditions,
        missingTermsValidation: this.missingTermsValidation(),
        termsData,
      };

      // Wraps the input component in a container, without mutating it. Good!
      return (
        <WrappedComponent
          TermsAndConditionsComponent={TermsAndConditionsBuilder({
            shouldShowTermsAndConditions,
            termsData,
            selectedTerms,
            hasAcceptedTerms: !!hasAcceptedTerms,
            onCheckTermsAndConditions: this.onCheckTermsAndConditions,
            t,
          })}
          {...enhancedProps}
          {...this.state}
        />
      );
    }
  };

interface TermsAndConditionsBuilder {
  shouldShowTermsAndConditions: boolean;
  termsData: TermsAndConditionsPerMarket[];
  selectedTerms: string[];
  hasAcceptedTerms: boolean;
  onCheckTermsAndConditions: (termUrl: string) => () => void;

  t: (key: string) => string;
}

interface TermsAndConditionsComponentProps {
  isLoading: boolean;
  ctaClicked: boolean;
}

export type TermsAndConditionsComponent = React.FC<TermsAndConditionsComponentProps>;

const TermsAndConditionsBuilder = ({
  shouldShowTermsAndConditions,
  termsData,
  selectedTerms,
  hasAcceptedTerms,
  onCheckTermsAndConditions,
  t,
}: TermsAndConditionsBuilder): React.FC<TermsAndConditionsComponentProps> => ({
  isLoading,
  ctaClicked,
}: TermsAndConditionsComponentProps): ReactElement | null => {
  const noTermsAcceptedError: string | undefined =
    shouldShowTermsAndConditions && ctaClicked && selectedTerms.length !== termsData.length
      ? t('products-fe.errors.noTermsAccepted')
      : undefined;

  return (
    <>
      {shouldShowTermsAndConditions && (
        <TermsAndConditions
          termsData={termsData}
          selectedTerms={selectedTerms}
          hasAcceptedTerms={hasAcceptedTerms}
          isLoading={isLoading}
          onChange={onCheckTermsAndConditions}
          t={t}
        />
      )}
      {noTermsAcceptedError && <ErrorMessage message={noTermsAcceptedError} />}
      {hasAcceptedTerms && <AlertBlock content={[t('products-fe.errors.termsAlreadyAccepted')]} />}
      {termsData.length > 0 && <SpacerVertical spaceToken="space/300" />}
    </>
  );
};
